import { Box, Icon, Theme, Typography } from "@outschool/backpack";
import * as FilestackUrls from "@outschool/filestack-urls";
import {
  ClassroomPostFilter,
  ClassroomPostOrder,
  ClassroomQuery,
  ClassroomQueryVariables,
  ClassroomTimeBoundsQuery,
  ClassroomTimeBoundsQueryVariables
} from "@outschool/gql-frontend-generated";
import { faExclamationCircle } from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { SupportRoutes } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import {
  ApolloError,
  gql,
  useMutation,
  useQueryWithPreviousData
} from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { Activity, MeetingReviewType } from "@outschool/gql-backend-generated";
import {
  ClassroomCard as Card,
  ClassRecapPreview,
  ClassroomLearnerList,
  LiveMeetingButton,
  PageSelect,
  Pagination,
  PreActivityReviewModal
} from "@outschool/ui-components-classroom";
import { ExternalLink } from "@outschool/ui-components-shared";
import { ErrorBoundary, Loading } from "@outschool/ui-legacy-component-library";
import { Redirect, useIsMobile } from "@outschool/ui-utils";
import _ from "lodash";
import React, { useRef } from "react";
import { useLocation, useParams } from "react-router-dom";

import { isFlexSchedule } from "../../shared/Activity";
import { useAddClassPostMutation } from "../../shared/client/hooks/useAddClassPostMutation";
import { useDeleteClassPostCommentMutation } from "../../shared/client/hooks/useDeleteClassPostCommentMutation";
import * as Enrollment from "../../shared/Enrollment";
import * as Env from "../../shared/Env";
import * as Routes from "../../shared/Routes";
import * as Section from "../../shared/Section";
import ActivityStatusLabel from "../components/ActivityStatusLabel";
import ClassPostList from "../components/classroom/ClassPostList";
import EditClassPost from "../components/classroom/EditClassPost";
import OneOnOneClassroomEducatorViewSidebar from "../components/classroom/OneOnOneClassroomEducatorViewSidebar";
import TipsForTeachingOnlineClasses from "../components/classroom/TipsForTeachingOnlineClasses";
// @ts-ignore
import EnrolledClassHeader from "../components/enrolled/EnrolledClassHeader";
import PickLearner from "../components/enrolled/PickLearner";
import { EnrollmentList } from "../components/enrollment/EnrollmentList";
import ErrorMessage from "../components/ErrorMessage";
import FirstTimeLearnersVideoModal from "../components/FirstTimeLearnersVideoModal";
import HeaderTags from "../components/HeaderTags";
import SimpleLayout from "../components/layouts/SimpleLayout";
import Link from "../components/Link";
import SectionManageToolbar from "../components/nav/SectionManageToolbar";
import StickyMobileHeader from "../components/nav/StickyMobileHeader";
import Toolbar from "../components/Toolbar";
import { useAddClassPostCommentMutation } from "../hooks/useAddClassPostCommentMutation";
import { UserMode, useLastUserMode } from "../lib/UserMode";
import { useTimeZone } from "../providers/TimeZoneProvider";
import {
  updateClassroomCache,
  useUpdateClassPostMutation
  // import-sort-ignore
  // @ts-ignore
} from "../queries/ClassPostMutations";
import NotFoundPage from "./NotFoundPage";
import { useTrackEvent } from "@outschool/ui-analytics";
import { meetingReviewMutation } from "../queries/MeetingReviewMutation";
import useCanReviewUpcomingMeeting from "../hooks/useCanReviewUpcomingMeeting";

const classroomTimeBoundsQuery = gql`
  query ClassroomTimeBounds($sectionUid: ID!) {
    classroom(sectionUid: $sectionUid) {
      uid
      section {
        uid
        start_time
        end_time
      }
      currentUserEnrollments {
        uid
        ongoing_start_time
        ongoing_end_time
      }
    }
  }
`;

export const classroomQuery = gql`
  query Classroom(
    $sectionUid: ID!
    $learnerUid: ID!
    $hasLearner: Boolean!
    $startBefore: DateTime!
    $start: DateTime
    $end: DateTime
    $plainText: Boolean
    $postFilter: ClassroomPostFilter
    $postOrder: ClassroomPostOrder
  ) {
    classroom(sectionUid: $sectionUid) {
      uid
      usersCanHaveLiveMeeting
      currentUserEnrollments {
        uid
        ongoing_start_time
        ongoing_end_time
        isEnded
        isPaused
        usersCanCommunicate
        learner {
          uid
          name
          age
          avatar
        }
      }
      section {
        uid
        start_time
        end_time
        currentUserCanManage
        filledSpaceCount
        usesOutschoolVideoChat
        nextOngoingMeeting {
          uid
          start_time
        }
        lastMeeting: meetings(
          startBefore: $startBefore
          limit: 1
          sortOrder: descending
        ) {
          uid
          start_time
          end_time
        }
        latestProgressReportDate
        isBuyable
        ...SectionManageToolbar_section
      }
      activity {
        uid
        user_uid
        title
        titleTranslated
        is_ongoing_weekly
        duration_weeks
        weekly_meetings
        is_online
        details {
          photo
        }
        leader {
          uid
          photo
        }
        hasTeacherSchedule
        ...ActivityStatusLabel_activity
        ...SectionManageToolbar_activity
      }
      learners {
        ...ClassroomLearnerList_profile
      }
      ...ClassPostList_classroom
    }
    learner(learnerUid: $learnerUid) @include(if: $hasLearner) {
      uid
      name
      ...ClassPostList_learner
    }
  }
  ${ActivityStatusLabel.fragments.activity}
  ${ClassPostList.fragments.classroom}
  ${ClassPostList.fragments.learner}
  ${SectionManageToolbar.fragments.activity}
  ${SectionManageToolbar.fragments.section}
  ${ClassroomLearnerList.fragments.profile}
`;

export default function Classroom({ ...layoutProps }) {
  const { sectionUid } = useParams<{ sectionUid: string }>();
  const currentDate = React.useMemo(() => {
    return new Date();
  }, []);

  const { data } = useQueryWithPreviousData<
    ClassroomTimeBoundsQuery,
    ClassroomTimeBoundsQueryVariables
  >(classroomTimeBoundsQuery, {
    variables: {
      sectionUid
    }
  });

  if (!data) {
    return <OutageJoinLiveMeetingButton />;
  }

  const lowerBound =
    _.min(
      data.classroom.currentUserEnrollments.map(e => e.ongoing_start_time)
    ) ?? data.classroom.section.start_time;
  const upperBound =
    _.max(data.classroom.currentUserEnrollments.map(e => e.ongoing_end_time)) ??
    data.classroom.section.end_time;

  return (
    <TimeBoundedClassroom
      lowerBound={lowerBound}
      upperBound={upperBound}
      currentDate={currentDate}
      {...layoutProps}
    />
  );
}

function OutageJoinLiveMeetingButton() {
  const location = useLocation();
  const learnerUid = location?.query?.learnerUid ?? "";
  const { sectionUid } = useParams<{ sectionUid: string }>();

  const pickLearner = !learnerUid && (
    <PickLearner sectionUid={sectionUid} hideForAdmin={true} />
  );
  return (
    <>
      {pickLearner}
      <Box
        sx={{
          maxWidth: "750px",
          position: "relative",
          width: "100%",
          marginLeft: "auto",
          marginRight: "auto",
          boxSizing: "border-box",
          backgroundColor: "#FFF",
          marginBottom: "2em"
        }}
      >
        <LiveMeetingButtonWithVideo
          sectionUid={sectionUid}
          learnerUid={learnerUid}
          canManage={false}
        />
      </Box>
    </>
  );
}

function parsePostFilterQueryParam(postFilter: string | undefined | null) {
  switch (postFilter) {
    case "lessons":
      return ClassroomPostFilter.OnlyForLessons;
    case "announcements":
      return ClassroomPostFilter.Announcements;
    default:
      return ClassroomPostFilter.All;
  }
}

function TimeBoundedClassroom({
  lowerBound,
  upperBound,
  currentDate,
  ...layoutProps
}: $TSFixMe) {
  const pageOptions = Pagination.monthOptionsForRange(lowerBound, upperBound);

  const location = useLocation();
  const learnerUid = location?.query?.learnerUid ?? "";
  const postFilter = parsePostFilterQueryParam(location?.query?.postFilter);
  const showLessonUI = postFilter === ClassroomPostFilter.OnlyForLessons;
  const { sectionUid } = useParams<{ sectionUid: string }>();
  const { timeZone: userTimeZone } = useTimeZone();
  const { currentUser, currentUserIsLoading, isAdmin } = useSession();
  const { t } = useTranslation("client\\routes\\Classroom");

  const [timeBounds, setTimeBounds] = React.useState<Pagination.TimeBounds>(
    pageOptions[0].value
  );

  const isMobile = useIsMobile();

  const topOfClassroomRef = React.useRef<HTMLDivElement>();
  const classroomQueryVariables: ClassroomQueryVariables = {
    sectionUid,
    learnerUid,
    hasLearner: Boolean(learnerUid),
    startBefore: currentDate,
    start: timeBounds?.start,
    end: timeBounds?.end,
    plainText: false,
    postFilter,
    postOrder: showLessonUI
      ? ClassroomPostOrder.Chronological
      : ClassroomPostOrder.ReverseChronological
  };

  const { data, loading, error } = useQueryWithPreviousData<
    ClassroomQuery,
    ClassroomQueryVariables
  >(classroomQuery, {
    fetchPolicy: "cache-and-network",
    variables: classroomQueryVariables
  });

  const classroom = data?.classroom;

  const pickLearner = !learnerUid && (
    <PickLearner sectionUid={sectionUid} hideForAdmin={true} />
  );
  const isInitialLoading =
    (loading && !classroom) || (currentUserIsLoading && !currentUser);

  const [lastUserMode] = useLastUserMode();

  if (isInitialLoading) {
    return (
      <SimpleLayout
        hideFooter
        hideReviewReminder
        // @ts-ignore TS(2322): Type 'null' is not assignable to type 'UserMode | ... Remove this comment to see the full error message
        userMode={lastUserMode}
        {...layoutProps}
      >
        <Loading />
        <OutageJoinLiveMeetingButton />
      </SimpleLayout>
    );
  }

  if (!error && !loading && !classroom) {
    return <NotFoundPage />;
  }

  const hasFailedToLoad = !isInitialLoading && (!currentUser || !classroom);

  if (error || hasFailedToLoad || !classroom) {
    return (
      <SimpleLayout
        hideFooter
        hideReviewReminder
        // @ts-ignore TS(2322): Type 'null' is not assignable to type 'UserMode | ... Remove this comment to see the full error message
        userMode={lastUserMode}
        {...layoutProps}
      >
        <ErrorMessage
          value={error}
          showStatusPageLink={Env.IS_READ_ONLY_MODE}
        />
        <OutageJoinLiveMeetingButton />
      </SimpleLayout>
    );
  }

  let learner = data?.learner;
  const learners = classroom?.learners;
  const activity = classroom?.activity;
  const currentUserEnrollments = classroom?.currentUserEnrollments;
  const usersCanCommentOnClass = classroom?.usersCanComment;
  const section = classroom?.section;
  const mentionNames = classroom?.mentionNames;
  const canManage = section.currentUserCanManage;
  const lastMeeting = section.lastMeeting[0];
  const lastMeetingTime = lastMeeting?.start_time ?? null;
  const lastProgressReportDate = section.latestProgressReportDate ?? null;
  const shouldShowProgressReportReminder =
    (activity.hasTeacherSchedule &&
      ((lastProgressReportDate !== null &&
        lastMeetingTime !== null &&
        dayjs(lastMeetingTime).isAfter(dayjs(lastProgressReportDate))) ||
        (lastMeetingTime && !lastProgressReportDate))) ??
    false;

  const activeEnrollments = currentUserEnrollments.filter(
    e => !Enrollment.isOngoing(e) || !(e.isEnded || e.isPaused)
  );
  learner = learner || activeEnrollments[0]?.learner;
  const singleEnrollment = learner
    ? currentUserEnrollments.find(
        e => e.learner && e.learner.uid === learner!.uid // learner is checked above
      )
    : currentUserEnrollments.length === 1 && currentUserEnrollments[0];

  const imageUrl = FilestackUrls.imageUrl(activity.details.photo, {
    w: 750,
    h: 395,
    fit: "crop"
  });
  const usersCanComment =
    usersCanCommentOnClass &&
    (canManage ||
      (!!currentUserEnrollments.length &&
        currentUserEnrollments.some(e => e.usersCanCommunicate)));
  const senderLearner = canManage ? undefined : learner;

  if (activity.isClub) {
    return <Redirect to={Routes.clubPath(section.uid)} />;
  }

  const sectionFullDurationString = Section.fullDurationString({
    activity,
    section,
    nextOngoingMeeting: section.nextOngoingMeeting,
    userTimeZone
  });

  const activityTitle = activity.titleTranslated;

  const currentUserIsSectionLeaderOrSellerOrgOwner =
    section.leader?.uid === currentUser?.uid ||
    activity.user_uid === currentUser?.uid;

  return (
    <SimpleLayout
      {...layoutProps}
      userMode={canManage ? UserMode.Educator : UserMode.Parent}
      grayBackground
      hideFooter
      hideReviewReminder
    >
      <HeaderTags
        title={t(
          "Classroom - {{activityTitle}} - {{sectionFullDurationString}}",
          { activityTitle, sectionFullDurationString }
        )}
      />
      <StickyMobileHeader
        leftChildren={
          section?.is_self_paced ? null : (
            <LiveMeetingButtonWithVideo
              activity={activity as Activity}
              sectionUid={sectionUid}
              learnerUid={learnerUid}
              learnerName={learner?.name ?? ""}
              userTimeZone={userTimeZone}
              canManage={canManage}
            />
          )
        }
      />
      {isAdmin && (
        <Toolbar>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              maxWidth: "1024px",
              margin: "0 auto",
              padding: "0 1em",
              marginBottom: "1em",
              textAlign: "center"
            }}
          >
            <ActivityStatusLabel activity={activity} />
            <Link to={Routes.sectionLearnersPath(section)}>
              {t("Manage this Section")}
            </Link>
          </Box>
        </Toolbar>
      )}
      <div>
        {canManage ? (
          <SectionManageToolbar
            activity={activity}
            section={section}
            learner={learners[0]}
          />
        ) : (
          <EnrolledClassHeader
            nonGiftEnrollments={currentUserEnrollments}
            activity={activity}
            section={section}
            leader={activity.leader}
            imageUrl={imageUrl}
            userTimeZone={userTimeZone}
            currentUser={currentUser}
            location={location}
            singleEnrollment={singleEnrollment}
          />
        )}
        <Box
          sx={(theme: $TSFixMe) => ({
            display: isMobile ? "initial" : "grid",
            position: "relative",
            gridGap: "1em",
            gridTemplateColumns: "minmax(0, 1fr) min-content",

            [theme.breakpoints.up("lg")]: {
              gridTemplateColumns: "70% 30%"
            },

            paddingX: "1em",
            width: "100%",
            maxWidth: 1100,
            margin: "0 auto"
          })}
        >
          <Box>
            <Box
              sx={(theme: Theme) => ({
                backgroundColor: "#FFF",
                paddingY: "2em",
                paddingX: "1em",
                [theme.breakpoints.up("md")]: {
                  paddingX: "2em"
                }
              })}
            >
              {learner ? (
                <Box
                  sx={{
                    marginBottom: "0.5em"
                  }}
                >
                  {t("Hi, {{currentUserName}}!", {
                    currentUserName: currentUser?.name ?? "there"
                  })}
                </Box>
              ) : (
                <Box
                  sx={{
                    marginBottom: "0.5em"
                  }}
                >
                  {currentUser
                    ? t("Hi, {{currentUserName}}!", {
                        currentUserName: currentUser.name
                      })
                    : t("Hi, there!")}
                </Box>
              )}
              {showLessonUI
                ? t(
                    "This page shows all your posts that are linked to a lesson in your syllabus."
                  )
                : t(
                    "This page is for class announcements and discussions. Please \
refresh the page to see the latest attendance information."
                  )}
            </Box>
            {!showLessonUI && (
              <>
                {!section?.is_self_paced && (
                  <Box
                    sx={{
                      marginBottom: "2em"
                    }}
                  >
                    <LiveMeetingButtonWithVideo
                      activity={activity as Activity}
                      sectionUid={sectionUid}
                      learnerUid={learnerUid}
                      learnerName={learner?.name ?? ""}
                      userTimeZone={userTimeZone}
                      canManage={canManage}
                    />
                  </Box>
                )}
                {lastMeeting && (
                  <ClassRecapPreview
                    meetingUid={lastMeeting.uid}
                    sectionUid={classroom.section.uid}
                    meetingEndTime={lastMeeting.end_time}
                    withNoteMakerMessage={shouldShowProgressReportReminder}
                    hideEditMarketingSettings={
                      currentUser?.sellerOrg?.currentUserIsTeacher === true
                    }
                  />
                )}
                {canManage && (
                  <SafetyReminders
                    isFlexSchedule={isFlexSchedule(classroom.activity)}
                  />
                )}
              </>
            )}
            {pickLearner}

            <Box ref={topOfClassroomRef} />
            {currentUser && (
              <AddPostOrComment
                section={section}
                senderLearner={senderLearner}
                mentionNames={mentionNames}
                classroom={classroom}
                usersCanComment={usersCanComment}
                classroomQueryVariables={classroomQueryVariables}
                showLessonUI={showLessonUI}
              />
            )}
            <PageSelect
              pageOptions={pageOptions}
              onChange={newBounds => {
                setTimeBounds(newBounds);
                if (topOfClassroomRef.current) {
                  setTimeout(
                    () =>
                      window.scrollTo({
                        behavior: "smooth",
                        top: topOfClassroomRef.current!.offsetTop
                      }),
                    150
                  );
                }
              }}
            />
          </Box>
          <ErrorBoundary>
            {canManage ? (
              activity.hasTeacherSchedule ? (
                <OneOnOneClassroomEducatorViewSidebar sectionUid={sectionUid} />
              ) : (
                <EnrollmentList
                  sectionUid={sectionUid}
                  showEnrollOwnLearner={
                    currentUserIsSectionLeaderOrSellerOrgOwner &&
                    section.isBuyable
                  }
                />
              )
            ) : (
              <ClassroomLearnerList learners={learners} />
            )}
          </ErrorBoundary>
        </Box>
      </div>
    </SimpleLayout>
  );
}

type SafetyRemindersProps = {
  isFlexSchedule: boolean;
};

const SafetyReminders: React.FunctionComponent<SafetyRemindersProps> = ({
  isFlexSchedule
}) => {
  const { t } = useTranslation("client\\routes\\Classroom");
  return (
    <Card
      sx={{
        display: "flex",
        flexDirection: "row",
        padding: 0
      }}
    >
      <Box
        sx={{
          width: "8px",
          background: "#F0C135",
          borderRadius: "8px 0px 0px 8px"
        }}
      />
      <Box
        sx={{
          padding: "2em",
          width: "100%"
        }}
      >
        <Box flex>
          <Icon
            sx={{
              color: "#F0C135",
              fontSize: "24px"
            }}
            icon={faExclamationCircle}
          />{" "}
          <Typography
            sx={{
              marginLeft: "0.5em"
            }}
            variant="h4"
          >
            {t("Safety Reminders")}
          </Typography>
          <ExternalLink
            style={{ marginLeft: "auto" }}
            url={SupportRoutes.learnerSafetyAndPrivacyForTeachersUrl()}
          >
            {t("Learn more")}
          </ExternalLink>
        </Box>
        <Box
          sx={{
            paddingLeft: "3em"
          }}
        >
          <ul>
            {!isFlexSchedule && (
              <Trans t={t}>
                <li>
                  Compare waiting room names in Zoom with enrolled learners
                </li>
                <li>
                  Admit learners one-by-one - never click &quot;Admit All&quot;
                </li>
                <li>Verify your learners are kids with a video check-in</li>
              </Trans>
            )}
            <li>
              {t(
                "Don’t encourage learners to share Personal Identifying Information \
(like emails or social handles)"
              )}
            </li>
            {!isFlexSchedule && (
              <li>{t("Don’t share zoom links — even if parents ask!")}</li>
            )}
            <li>
              <Trans t={t}>
                When in doubt,{" "}
                <ExternalLink url={"mailto:safety@outschool.com"}>
                  reach out!
                </ExternalLink>
              </Trans>
            </li>
          </ul>
        </Box>
      </Box>
    </Card>
  );
};

function AddPostOrComment({
  section,
  senderLearner,
  mentionNames,
  classroom,
  usersCanComment,
  classroomQueryVariables,
  showLessonUI
}: $TSFixMe) {
  const { currentUser } = useSession();
  const [addPostError, setAddPostError] = React.useState<ApolloError>();
  const { t } = useTranslation("client\\routes\\Classroom");

  const canManage = section.currentUserCanManage;

  const { activity, usersCanPost } = classroom;

  const { addClassPost } = useAddClassPostMutation({
    // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message
    sender: currentUser,
    classroomQueryVariables,
    queryToUpdate: classroomQuery
  });

  const addClassPostComment = useAddClassPostCommentMutation({
    // @ts-ignore TS(2322): Type '{ __typename?: "User" | undefined; uid: stri... Remove this comment to see the full error message
    currentUser,
    classroomQueryVariables,
    updateClassroomCache
  });

  const deleteClassPostComment = useDeleteClassPostCommentMutation({
    updateQuery: classroomQuery,
    updateQueryVariables: classroomQueryVariables
  });

  const updateClassPost = useUpdateClassPostMutation({
    updateQuery: classroomQuery,
    updateQueryVariables: classroomQueryVariables
  });

  const handleDeleteComment = React.useCallback(
    async (commentUid: $TSFixMe, postUid: $TSFixMe) => {
      await deleteClassPostComment({ commentUid, postUid });
    },
    [deleteClassPostComment]
  );

  const handleUpdateClassPost = React.useCallback(
    async (
      post: $TSFixMe,
      text: $TSFixMe,
      textMimeType: $TSFixMe,
      attachments: $TSFixMe,
      video: $TSFixMe,
      publishAt: $TSFixMe
    ) => {
      await updateClassPost(
        post,
        text,
        textMimeType,
        attachments,
        video,
        publishAt
      );
    },
    [updateClassPost]
  );

  const handleAddComment = React.useCallback(
    async (
      postUid: $TSFixMe,
      text: $TSFixMe,
      attachments: $TSFixMe,
      video: $TSFixMe,
      mentions: $TSFixMe
    ) => {
      await addClassPostComment(
        postUid,
        text,
        attachments,
        video,
        senderLearner,
        mentions
      );
    },
    [addClassPostComment, senderLearner]
  );

  const handleAddPost = React.useCallback(
    async (
      text: $TSFixMe,
      textMimeType: $TSFixMe,
      attachments: $TSFixMe,
      video: $TSFixMe,
      publishAt: $TSFixMe,
      mentions = []
    ) => {
      await addClassPost({
        text,
        textMimeType,
        attachments,
        video,
        publishAt,
        mentions
      }).catch(err => setAddPostError(err));
    },
    [addClassPost]
  );
  return (
    <>
      {!section.is_self_paced && canManage && usersCanPost && !showLessonUI && (
        <Box
          sx={{
            position: "relative",
            padding: "1em",
            backgroundColor: "common.white",
            marginBottom: "2em",
            border: "1px solid",
            borderColor: "grey.100"
          }}
          data-test-id="add-new-post"
        >
          <ErrorMessage
            value={addPostError}
            showStatusPageLink={Env.IS_READ_ONLY_MODE}
          />
          <EditClassPost
            inline
            formTitle={t("Make an Announcement")}
            hasCopyExistingPostButton
            buttonLabel={t("Send")}
            section={section}
            activity={activity}
            onPost={handleAddPost}
            mentionNames={mentionNames}
          />
        </Box>
      )}
      <ErrorBoundary>
        <ClassPostList
          classroom={classroom}
          learner={senderLearner}
          usersCanComment={usersCanComment}
          addClassPostComment={handleAddComment}
          deleteClassPostComment={handleDeleteComment}
          updateClassPost={handleUpdateClassPost}
          showLessonUI={showLessonUI}
        />
      </ErrorBoundary>
    </>
  );
}

type LiveMeetingButtonWithVideoProps = {
  activity?: Activity;
  sectionUid: string;
  learnerUid: string;
  learnerName?: string;
  userTimeZone?: string | null;
  canManage: boolean;
  shouldShowOnlyButton?: boolean;
  joinButtonLabel?: string;
  size?: string;
};

export const LiveMeetingButtonWithVideo = ({
  activity,
  sectionUid,
  learnerUid,
  learnerName,
  userTimeZone,
  canManage,
  shouldShowOnlyButton = false,
  joinButtonLabel,
  size
}: LiveMeetingButtonWithVideoProps) => {
  const trackEvent = useTrackEvent();
  const [showPreActivityReviewModal, setShowPreActivityReviewModal] =
    React.useState(false);
  const joinCallbackRef = useRef<() => void>(() => {});

  const [submitMeetingReview] = useMutation(meetingReviewMutation);

  const canReview = useCanReviewUpcomingMeeting(sectionUid, learnerUid);
  const [hasSubmittedReview, setHasSubmittedReview] = React.useState(false);

  const shouldShowPreActivityReviewModal =
    activity && learnerName && canReview && !hasSubmittedReview;

  const onClickJoinMeeting = (joinCallback: () => void) => {
    if (shouldShowPreActivityReviewModal) {
      joinCallbackRef.current = joinCallback;
      setShowPreActivityReviewModal(true);
    } else {
      joinCallback();
    }
  };

  const onJoinAfterReview = (rating: number) => {
    joinCallbackRef.current();
    setShowPreActivityReviewModal(false);
    setHasSubmittedReview(true);
    trackEvent("pre_activity_review_submit_review_touch", { rating });

    if (rating > 0) {
      try {
        submitMeetingReview({
          variables: {
            sectionUid,
            rating,
            type: MeetingReviewType.Pre,
            learnerUid
          }
        });
      } catch (error) {
        OsPlatform.captureError(error);
      }
    }
  };

  const button = (
    <LiveMeetingButton
      sectionUid={sectionUid}
      learnerUid={learnerUid}
      sectionJoinPathFn={Routes.sectionJoinPath}
      userTimeZone={userTimeZone ?? undefined}
      onClickJoinMeeting={onClickJoinMeeting}
      shouldShowNextMeetingMessage={!shouldShowOnlyButton}
      joinButtonLabel={joinButtonLabel}
      size={size}
    />
  );

  return (
    <>
      {showPreActivityReviewModal && shouldShowPreActivityReviewModal && (
        <PreActivityReviewModal
          activity={activity}
          learnerName={learnerName}
          onJoin={onJoinAfterReview}
          onSelectRating={rating =>
            trackEvent("pre_activity_review_set_rating", { rating })
          }
          onMount={() => trackEvent("pre_activity_review_modal_view")}
          onClose={() => setShowPreActivityReviewModal(false)}
        />
      )}

      {shouldShowOnlyButton ? (
        button
      ) : (
        <Box
          sx={{
            maxWidth: "750",
            position: "relative",
            width: "100%",
            marginLeft: "auto",
            marginRight: "auto",
            boxSizing: "border-box",
            backgroundColor: "#f1f2f3",
            paddingY: "0.75em",
            textAlign: "center",
            borderBottom: "1px solid #e5e5e5"
          }}
        >
          {button}
          {canManage ? (
            <TipsForTeachingOnlineClasses />
          ) : (
            <FirstTimeLearnersVideoModal />
          )}
        </Box>
      )}
    </>
  );
};
